import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Button } from '@repo/ui/src/components/ui/button';
import { Skeleton } from '@repo/ui/src/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/src/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@repo/ui/src/components/ui/alert-dialog';
import { type AttendanceStatus } from '@repo/shared-types';
import { useIsMobile } from '@repo/ui/src/hooks/use-mobile';
import { cn } from '@repo/ui/src/lib/utils';
import {
  useSession, useCancelSession, useAddParticipant, useRemoveParticipant,
  useMarkAttendance, useReassignSessionTeacher, useUpdateSessionTeachers,
} from '../../hooks/useSessions';
import { useServerNow } from '../../hooks/useServerNow';
import { useEnrollmentsForSubject } from '../../hooks/useEnrollmentsForSubject';
import { useShowTeacher, useTeacherOptions } from '../../hooks/useTeachers';
import { useMembers } from '../../hooks/useTeam';
import { useCan } from '../../hooks/useCan';
import { AdminOnlyTooltip } from '../../components/AdminOnlyTooltip';
import { AttendanceRow } from '../../components/AttendanceRow';
import { TeacherAdhocExplanationPopover } from '../../components/ModeExplanationPopover';
import { AddStudentCombobox } from '../../components/AddStudentCombobox';
import { SelectionBar } from '../../components/SelectionBar';
import { BusyButton } from '../../components/BusyButton';
import { resolveTeacher } from '../../lib/teacherDisplay';
import { TeacherListEditor } from '../../components/TeacherListEditor';
import { fmtSessionTime, fmtDayLabel } from '../../lib/sessionTime';
import { isSessionLocked } from '../../lib/attendanceLock';
import { attendanceProgress } from '../../lib/attendanceProgress';
import { PageHeader } from '../../components/PageHeader';
import { SessionNotesPanel } from '../../components/notes/SessionNotesPanel';
import { SessionNotesEntryRow } from '../../components/notes/SessionNotesEntryRow';
import { useSessionNotes } from '../../hooks/useProgress';

type AttendanceMark = AttendanceStatus;

export function SessionDetailPage() {
  const { t } = useTranslation('scheduling');
  const navigate = useNavigate();
  const { id } = useParams();
  const sessionId = id ?? null;
  const { data: s } = useSession(sessionId);
  const cancel = useCancelSession();
  const add = useAddParticipant(sessionId ?? '');
  const remove = useRemoveParticipant(sessionId ?? '');
  const mark = useMarkAttendance(sessionId ?? '');
  const { data: subjectEnrollments } = useEnrollmentsForSubject(s?.subjectId ?? null);
  const showTeacher = useShowTeacher();
  const teacherOptions = useTeacherOptions();
  const serverNow = useServerNow(); // follows the (maybe simulated) server clock, not the browser's
  // includeRemoved: this session's teacherId may point at a since-removed
  // teacher — their name must still resolve (the reassign picker below uses
  // the active-only teacherOptions, so they can't be re-picked).
  const { data: members } = useMembers({ includeRemoved: true });
  const reassign = useReassignSessionTeacher(sessionId ?? '');
  const updateTeachers = useUpdateSessionTeachers(sessionId ?? '');
  const canManageSessions = useCan('manage:sessions');
  const [marks, setMarks] = useState<Record<string, AttendanceMark>>({});
  const [removeTarget, setRemoveTarget] = useState<string | null>(null);
  const [officialInfoOpen, setOfficialInfoOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesFocusStudentId, setNotesFocusStudentId] = useState<string | null>(null);
  // Called with the other hooks, above the skeleton early-return (rules of hooks).
  const isMobile = useIsMobile();
  // Fetched unconditionally for a COMPLETED session so the entry row can show
  // its filled/empty state without the teacher opening the panel first.
  const { data: sessionNotes } = useSessionNotes(s?.status === 'COMPLETED' ? sessionId : null);

  // initialize from current attendance when session loads
  useEffect(() => {
    if (!s) return;
    const init: Record<string, AttendanceMark> = {};
    for (const p of s.participants) if (p.attendance !== 'PENDING') init[p.enrollmentId] = p.attendance;
    setMarks(init);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [s?.id]);

  if (!s) {
    return (
      <div className="p-8 max-w-2xl mx-auto w-full">
        <div className="space-y-2">
          <Skeleton className="h-4 w-16" />
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </div>
        <div className="mt-6 space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center justify-between gap-2 rounded-md border border-surface-200 bg-surface-0 px-3 py-2">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-8 w-40" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const inSession = new Set(s.participants.map((p) => p.enrollmentId));
  const addable = (subjectEnrollments ?? []).filter((e) => !inSession.has(e.id)); // e.id = enrollmentId
  const started = new Date(s.startAt).getTime() <= serverNow.getTime();
  const dirty = s.participants.some((p) => marks[p.enrollmentId] !== undefined && marks[p.enrollmentId] !== p.attendance);
  const progress = attendanceProgress(
    s.participants.map((p) => ({ attendance: marks[p.enrollmentId] ?? p.attendance })),
  );
  // Same rule the workbench card uses: the whole roster chốt sổ'd means there
  // is nothing here to save, so no footer and no bar.
  const locked = isSessionLocked(s.participants);

  // A chốt-sổ'd row cannot be saved — the attendance route's I5 guard rejects
  // the whole batch if one locked enrollment is in it — so locked rows stay
  // out of the payload and out of "Tất cả có mặt".
  const editable = s.participants.filter((p) => !p.attendanceLocked);

  const markAll = () => {
    const init: Record<string, AttendanceMark> = {};
    for (const p of editable) init[p.enrollmentId] = 'PRESENT';
    setMarks(init);
  };

  const save = () => {
    mark.mutate({
      marks: editable
        .filter((p) => marks[p.enrollmentId])
        .map((p) => ({ enrollmentId: p.enrollmentId, attendance: marks[p.enrollmentId]! })),
    });
  };

  return (
    <div className={cn('p-8 max-w-2xl mx-auto w-full', dirty && 'pb-32 md:pb-8')}>
      <PageHeader
        size="sm"
        back={{ label: t('session.back'), onClick: () => navigate(-1) }}
        title={s.subject.name}
        subtitleWrap
        subtitle={
          <>
            {fmtDayLabel(s.startAt)} · {fmtSessionTime(s.startAt, s.durationMin)} · {t(`sessions.status.${s.status}`)}
            {s.status === 'COMPLETED' && <span className="ml-2 text-emerald-600">· {t('attendance.done')}</span>}
          </>
        }
      />

      {progress.total > 0 && (
        <div className="mt-2">
          <span className="tabular text-[12px] text-surface-500">
            {t('attendance.progress', { marked: progress.marked, total: progress.total })}
          </span>
          <div className="mt-0.5 h-[2px] overflow-hidden rounded-full bg-surface-100">
            <div
              className="h-full rounded-full bg-brand-500 transition-[width] duration-200"
              style={{ width: `${progress.ratio * 100}%` }}
            />
          </div>
        </div>
      )}

      {s.status === 'COMPLETED' && (
        <SessionNotesEntryRow
          notes={sessionNotes}
          onOpen={() => { setNotesFocusStudentId(null); setNotesOpen(true); }}
        />
      )}

      {showTeacher ? (() => {
        const teacher = resolveTeacher(members ?? [], s.teacherId);
        return (
          <div className="mt-3 space-y-2">
            {/* Existing đứng-tên line + đổi-GV Select — the swap flow, unchanged. */}
            <div className="flex items-center gap-2">
              {teacher ? (
                <span className="inline-flex items-center gap-1.5 text-sm">
                  <span className="inline-block h-2 w-2 rounded-full shrink-0" style={{ background: teacher.color }} />
                  <span className="text-muted-foreground">{t('session.teacher')}:</span>
                  <span className="font-medium">{teacher.name}</span>
                  {/* teacherFromSlot is false both for a dạy-thay swap on a
                      slot-based session AND for every teacher on a purely
                      ad-hoc session (slotId null has no slot list to belong
                      to) — the same "Đột xuất" signal either way. */}
                  {s.teacherFromSlot === false && <TeacherAdhocExplanationPopover teacherName={teacher.name} />}
                </span>
              ) : null}
              <AdminOnlyTooltip show={!canManageSessions} className="ml-auto">
                <Select
                  value={s.teacherId}
                  onValueChange={(teacherId) => reassign.mutate(teacherId)}
                  disabled={reassign.isPending || !canManageSessions}
                >
                  <SelectTrigger className={canManageSessions ? 'ml-auto w-52' : 'w-52'} aria-label={t('session.changeTeacher')}>
                    <SelectValue placeholder={t('session.changeTeacher')} />
                  </SelectTrigger>
                  <SelectContent>
                    {teacherOptions.map((opt) => (
                      <SelectItem key={opt.id} value={opt.id}>
                        <span className="inline-flex items-center gap-2">
                          <span className="inline-block h-2 w-2 rounded-full" style={{ background: opt.color }} />
                          {opt.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </AdminOnlyTooltip>
            </div>
            <TeacherListEditor
              ownerTeacherId={s.teacherId}
              rows={s.teachers}
              onChange={(rows) => updateTeachers.mutate(rows.map((r) => ({ membershipId: r.membershipId, role: r.role })))}
              options={teacherOptions}
              disabled={!canManageSessions}
              // Names are visible to everyone who can open this page; money is
              // not on this object at all (SessionTeacher has no rate column).
              showOverride={false}
              mode="session"
              ns="scheduling"
            />
          </div>
        );
      })() : null}

      <div className="mt-6">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold">{t('session.participants')}</h2>
          {/* Phones get these in the sticky bar below once dirty — see the
              comment there. */}
          {!isMobile && (
            <div className="flex gap-2">
              {dirty && <Button variant="ghost" size="sm" onClick={() => setMarks({})} disabled={mark.isPending}>{t('attendance.discard')}</Button>}
              {started && <Button variant="outline" size="sm" onClick={markAll}>{t('attendance.markAllPresent')}</Button>}
              <Button size="sm" disabled={mark.isPending || !dirty} onClick={save}>
                {mark.isPending ? t('attendance.saving') : t('attendance.save')}
              </Button>
            </div>
          )}
          {/* Phones, before any tap: the sticky bar below only exists once
              `dirty`, so "Tất cả có mặt" needs a way to be reached before that
              too. Gone once everyone editable is already marked. */}
          {isMobile && !locked && !dirty && started && progress.marked < progress.total && editable.length > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll}>
              {t('attendance.markAllPresent')}
            </Button>
          )}
        </div>
        <div className="space-y-2">
          {s.participants.map((p) => (
            <AttendanceRow
              key={p.id}
              participant={p}
              current={marks[p.enrollmentId] ?? p.attendance}
              started={started}
              onMark={(next) => setMarks((m) => ({ ...m, [p.enrollmentId]: next }))}
              onRemove={() => (p.fromSlot === false ? setRemoveTarget(p.enrollmentId) : setOfficialInfoOpen(true))}
              adhocBadge={p.fromSlot === false}
              removeDisabled={remove.isPending || !canManageSessions}
              removeGuarded={!canManageSessions}
              hasNote={sessionNotes?.notedStudentIds.includes(p.studentId) ?? false}
              onOpenNote={
                s.status === 'COMPLETED'
                  ? () => { setNotesFocusStudentId(p.studentId); setNotesOpen(true); }
                  : undefined
              }
            />
          ))}
        </div>

        {/* Add a student to this session (makeup/join) — options are enrollments in this session's subject. */}
        <div className="mt-3 max-w-xs">
          <AdminOnlyTooltip show={!canManageSessions}>
            <AddStudentCombobox
              options={addable}
              onSelect={(enrollmentId) => add.mutate(enrollmentId)}
              disabled={!canManageSessions}
              placeholder={t('session.addStudent')}
            />
          </AdminOnlyTooltip>
          <p className="mt-1 text-xs text-muted-foreground">{t('session.addHint')}</p>
        </div>
      </div>

      <div className="mt-8 flex gap-2">
        {s.status !== 'CANCELLED' && (
          <AdminOnlyTooltip show={!canManageSessions}>
            <Button variant="outline" onClick={() => cancel.mutate(s.id)} disabled={!canManageSessions}>
              {t('session.cancel')}
            </Button>
          </AdminOnlyTooltip>
        )}
      </div>

      <SelectionBar
        id={`attendance-${s.id}`}
        ariaLabel={t('attendance.barAriaLabel')}
        active={isMobile && !locked && dirty}
        summary={
          <span className="tabular">
            {t('attendance.progress', { marked: progress.marked, total: progress.total })}
          </span>
        }
        actions={
          <>
            <Button variant="ghost" size="sm" onClick={() => setMarks({})} disabled={mark.isPending}>
              {t('attendance.discard')}
            </Button>
            {started && (
              <Button variant="outline" size="sm" onClick={markAll}>
                {t('attendance.markAllPresent')}
              </Button>
            )}
            <BusyButton size="sm" busy={mark.isPending} busyLabel={t('attendance.saving')} onClick={save}>
              {t('attendance.save')}
            </BusyButton>
          </>
        }
      />

      <AlertDialog open={removeTarget !== null} onOpenChange={(open) => !open && setRemoveTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('attendance.removeTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('attendance.removeDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('attendance.removeCancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={() => {
                if (removeTarget) remove.mutate(removeTarget);
                setRemoveTarget(null);
              }}
            >
              {t('attendance.removeConfirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={officialInfoOpen} onOpenChange={setOfficialInfoOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('attendance.officialRemoveTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('attendance.officialRemoveDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setOfficialInfoOpen(false)}>{t('attendance.close')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SessionNotesPanel
        // Keyed on the session id: SessionDetailPage does not remount when
        // navigating between sessions on the same route (classic React
        // Router behavior), so without this key a Panel opened for session A
        // could still be carrying A's draft state if the route swapped to
        // session B underneath it.
        key={s.id}
        session={s}
        open={notesOpen}
        onOpenChange={setNotesOpen}
        focusStudentId={notesFocusStudentId}
      />
    </div>
  );
}
